import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useClientStore } from '@stores/client';
import { EntityFullDetails } from '@/client/types/entity-full';

export interface Collection {
  ecolid: number;
  name: string;
  description?: string;
  private: number;
  createdAt: number;
  updatedAt?: number;
  itemCount?: number;
}

interface CollectionApiDto {
  ecolid: number;
  name: string;
  description?: string;
  date_added: string;
  date_modified?: string; // Optional since createCollection doesn't return it
  private: number; // Can be boolean or number (0/1)
  oid?: number; // Optional fields from createCollection response
  uid?: number;
}

type CollectionItemsResponse = Array<{
  ecolid: number;
  name: string;
  description: string;
  private: number;
  date_added: string;
  date_modified: string;
  entity: {
    page: number;
    per_page: number;
    results: Array<EntityFullDetails>;
    total_entities: number;
    total_pages: number;
  };
}>

interface UpdateCollectionResponse {
  collection: CollectionApiDto;
  success: string;
  updated_fields: string[];
}

type CollectionItemMap = Record<number, number[]>

interface CollectionsState {
  collections: Collection[];
  collectionItems: CollectionItemMap;
  isLoading: boolean;
  error: string | null;
  
  // Collection management
  fetchCollections: () => Promise<void>;
  createCollection: (name: string, description?: string, isPrivate?: number) => Promise<number | null>;
  updateCollection: (ecolid: number, name: string, description?: string, isPrivate?: number) => Promise<boolean>;
  deleteCollection: (ecolid: number) => Promise<boolean>;
  
  // Item management
  fetchCollectionItems: (ecolid: number) => Promise<number[]>;
  addItemToCollection: (eid: number, ecolid: number) => Promise<boolean>;
  removeItemFromCollection: (eid: number, ecolid: number) => Promise<boolean>;
  getCollectionItems: (ecolid: number) => number[];
  isItemInCollection: (eid: number, ecolid: number) => boolean;
  isItemInAnyCollection: (eid: number) => boolean;
  getItemCollections: (eid: number) => Collection[];
  
  // Count functions
  getItemCount: (ecolid: number) => number;
  getTotalItemCount: () => number;
  
  // Enhanced utility methods
  refreshCollectionData: (ecolid: number) => Promise<void>;
  getCollectionById: (ecolid: number) => Collection | null;
  isValidCollection: (ecolid: number) => boolean;
  
  // Bulk operations
  addMultipleItemsToCollection: (eids: number[], ecolid: number) => Promise<{total: number, successful: number, failed: number}>;
  removeMultipleItemsFromCollection: (eids: number[], ecolid: number) => Promise<{total: number, successful: number, failed: number}>;
  
  // Search and filtering
  searchCollections: (query: string) => Collection[];
  getPublicCollections: () => Collection[];
  getPrivateCollections: () => Collection[];
}

// Helper function to map API DTO to local Collection type
const mapDtoToCollection = (dto: CollectionApiDto): Collection => ({
  ecolid: dto.ecolid,
  name: dto.name,
  description: dto.description,
  private: dto.private || 0,
  createdAt: new Date(dto.date_added).getTime(),
  updatedAt: dto.date_modified ? new Date(dto.date_modified).getTime() : new Date(dto.date_added).getTime()
});

// Create the store
const createCollectionsStore = () => create<CollectionsState>()(
  persist(
    (set, get) => ({
      collections: [],
      collectionItems: {},
      isLoading: false,
      error: null,
      
      // Fetch all collections from the API
      fetchCollections: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await useClientStore.getState().request<APIResponse<CollectionApiDto[]>>('/api/v2/entity/collection?use_cache=0');
          if( response.status !== 200 )
            throw new Error( response.message || 'Failed to fetch collections' )

          const collections = response.data.map( mapDtoToCollection );
          
          set({ 
            collections,
            isLoading: false 
          });
          
          // Fetch items for each collection in parallel for better performance
          const itemFetchPromises = collections.map(collection => 
            get().fetchCollectionItems(collection.ecolid)
          );
          await Promise.allSettled(itemFetchPromises);
          
        } catch (error) {
          console.error('Error fetching collections:', error);
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to fetch collections' 
          });
        }
      },
      
      // Collection management
      createCollection: async (name, description, isPrivate = 0) => {
        set({ isLoading: true, error: null });
        try {
          const response = await useClientStore.getState().request<APIResponse<CollectionApiDto>>('/api/v2/entity/collection', 'POST', {
            name,
            description,
            private: isPrivate
          });
          if( response.status !== 201 )
            throw new Error(response.message || 'Create collection request failed')
          
          const newCollection = mapDtoToCollection(response.data);
          
          set((state) => ({
            collections: [...state.collections, newCollection],
            collectionItems: { ...state.collectionItems, [newCollection.ecolid]: [] },
            isLoading: false
          }));

          // No need to refresh all collections since we just created one
          // Just ensure the new collection's items are initialized
          await get().fetchCollectionItems(newCollection.ecolid);
          
          return newCollection.ecolid;
        } catch (error) {
          console.error('Error creating collection:', error);
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to create collection' 
          });
          return null;
        }
      },
      
      updateCollection: async (ecolid, name, description, isPrivate) => {
        set({ isLoading: true, error: null });
        try {
          const updateData: any = { name, description };
          if (isPrivate !== undefined) {
            updateData.private = isPrivate;
          }
          
          const response = await useClientStore.getState().request<APIResponse<UpdateCollectionResponse>>(`/api/v2/entity/collection/${ecolid}`, 'PUT', updateData);
          
          if( response.status !== 200 )
            throw new Error(response.message || 'Save updated collection request failed')
          
          set((state) => ({
            collections: state.collections.map((collection) =>
              collection.ecolid === ecolid
                ? mapDtoToCollection( response.data.collection )
                : collection
            ),
            isLoading: false
          }));

          // No need to refresh all collections since we just updated one locally
          
          return true;
        } catch (error) {
          console.error('Error updating collection:', error);
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to update collection' 
          });
          return false;
        }
      },
      
      deleteCollection: async (ecolid) => {
        set({ isLoading: true, error: null });
        try {
          const response = await useClientStore.getState().request<APIResponse<{ success: string }>>(`/api/v2/entity/collection/${ecolid}`, 'DELETE', {});
          
          if( response.status !== 200 )
            throw new Error(response.message || 'Delete collection request failed')
          
          set((state) => {
            const { [ecolid]: itemsToRemove, ...remainingItems } = state.collectionItems;
            
            return {
              collections: state.collections.filter((collection) => collection.ecolid !== ecolid),
              collectionItems: remainingItems,
              isLoading: false
            };
          });

          // No need to refresh all collections since we just deleted one locally
          
          return true;
        } catch (error) {
          console.error('Error deleting collection:', error);
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to delete collection' 
          });
          return false;
        }
      },
      
      // Item management
      fetchCollectionItems: async (ecolid) => {
        try {
          const response = await useClientStore.getState().request<APIResponse<CollectionItemsResponse>>(`/api/v2/entity/collection/entities?ecolid=${ecolid}&use_cache=0`);
          
          if( response.status !== 200 )
            throw new Error(response.message || 'Fetch collection items request failed')

          // Find the collection in the response array
          const collectionData = response.data.find(collection => collection.ecolid === ecolid);
          
          if (!collectionData || !collectionData.entity) {
            // If no collection found or no entity data, return empty array
            set((state) => ({
              collectionItems: {
                ...state.collectionItems,
                [ecolid]: [],
              },
            }));
            return [];
          }

          // Extract entity IDs from the paginated results
          const items = collectionData.entity.results.map(result => result.Entity.eid);
          
          set((state) => ({
            collectionItems: {
              ...state.collectionItems,
              [ecolid]: items,
            },
          }));
          
          return items;
        } catch (error) {
          console.error(`Error fetching items for collection ${ecolid}:`, error);
          return [];
        }
      },
      
      addItemToCollection: async (eid, ecolid) => {
        const currentItems = get().collectionItems[ecolid] || [];
        
        // Don't add if already exists
        if (currentItems.includes(eid)) {
          return true;
        }
        
        try {
          const response = await useClientStore.getState().request<APIResponse<{
            success: string;
          }>>(`/api/v2/entity/collection/add`, 'POST', { ecolid, eid });
          
          if( response.status !== 201 )
            throw new Error(response.message || 'Add collection item request failed')
          
          set((state) => ({
            collectionItems: {
              ...state.collectionItems,
              [ecolid]: [...currentItems, eid],
            },
          }));

          // Refresh collection items to ensure UI stays in sync
          await get().fetchCollectionItems(ecolid);
          
          return true;
        } catch (error) {
          console.error('Error adding item to collection:', error);
          return false;
        }
      },
      
      removeItemFromCollection: async (eid, ecolid) => {
        try {
          const response = await useClientStore.getState().request<APIResponse<{
            success: string;
          }>>(`/api/v2/entity/collection/remove?ecolid=${ecolid}&eid=${eid}`, 'DELETE', {});
          
          if( response.status !== 200 )
            throw new Error(response.message || 'Delete collection item request failed')
          
          set((state) => {
            const currentItems = state.collectionItems[ecolid] || [];
            
            return {
              collectionItems: {
                ...state.collectionItems,
                [ecolid]: currentItems.filter((id) => id !== eid),
              },
            };
          });

          // Refresh collection items to get the most up-to-date data
          await get().fetchCollectionItems(ecolid);
          
          return true;
        } catch (error) {
          console.error('Error removing item from collection:', error);
          return false;
        }
      },
      
      // Helper methods for better data consistency
      refreshCollectionData: async (ecolid: number) => {
        // Refresh both collection info and items
        await Promise.all([
          get().fetchCollections(),
          get().fetchCollectionItems(ecolid)
        ]);
      },
      
      // Enhanced item management with better error handling
      getCollectionItems: (ecolid) => {
        const items = get().collectionItems[ecolid];
        return Array.isArray(items) ? items : [];
      },
      
      isItemInCollection: (eid, ecolid) => {
        const items = get().getCollectionItems(ecolid);
        return items.includes(eid);
      },
      
      isItemInAnyCollection: (eid) => {
        return Object
                .entries( get().collectionItems )
                .some( ([ecolid, items]) => items.includes(eid) )
      },
      
      getItemCollections: (eid) => {
        const { collections, collectionItems } = get();
        
        // Find all collections that contain this item
        return collections.filter((collection) => {
          const items = collectionItems[collection.ecolid] || [];
          return items.includes(eid);
        });
      },
      
      // Enhanced count functions with validation
      getItemCount: (ecolid) => {
        const items = get().getCollectionItems(ecolid);
        return items.length;
      },
      
      getTotalItemCount: () => {
        const { collectionItems } = get();
        
        // Count unique items across all collections
        const uniqueItems = new Set<number>();
        
        Object.values(collectionItems).forEach(items => {
          if (Array.isArray(items)) {
            items.forEach(eid => {
              if (typeof eid === 'number') {
                uniqueItems.add(eid);
              }
            });
          }
        });
        
        return uniqueItems.size;
      },
      
      // Collection validation and utilities
      getCollectionById: (ecolid: number) => {
        const { collections } = get();
        return collections.find(collection => collection.ecolid === ecolid) || null;
      },
      
      isValidCollection: (ecolid: number) => {
        return get().getCollectionById(ecolid) !== null;
      },
      
      // Bulk operations for better performance
      addMultipleItemsToCollection: async (eids: number[], ecolid: number) => {
        const results = await Promise.allSettled(
          eids.map(eid => get().addItemToCollection(eid, ecolid))
        );
        
        const successful = results.filter(result => 
          result.status === 'fulfilled' && result.value === true
        ).length;
        
        return {
          total: eids.length,
          successful,
          failed: eids.length - successful
        };
      },
      
      removeMultipleItemsFromCollection: async (eids: number[], ecolid: number) => {
        const results = await Promise.allSettled(
          eids.map(eid => get().removeItemFromCollection(eid, ecolid))
        );
        
        const successful = results.filter(result => 
          result.status === 'fulfilled' && result.value === true
        ).length;
        
        return {
          total: eids.length,
          successful,
          failed: eids.length - successful
        };
      },
      
      // Collection search and filtering
      searchCollections: (query: string) => {
        const { collections } = get();
        const lowerQuery = query.toLowerCase();
        
        return collections.filter(collection => 
          collection.name.toLowerCase().includes(lowerQuery) ||
          (collection.description && collection.description.toLowerCase().includes(lowerQuery))
        );
      },
      
      getPublicCollections: () => {
        const { collections } = get();
        return collections.filter(collection => !collection.private);
      },
      
      getPrivateCollections: () => {
        const { collections } = get();
        return collections.filter(collection => collection.private);
      }
    }),
    {
      name: 'collections-storage',
      partialize: (state) => ({
        collections: state.collections,
        collectionItems: state.collectionItems,
      })
    }
  )
);

// Create the store instance
const collectionStoreInstance = createCollectionsStore();

// Create a custom hook for item collections with enhanced functionality
const createItemCollectionsHook = () => {
  return (eid: number) => {
    const store = collectionStoreInstance();
    const {
      addItemToCollection, 
      removeItemFromCollection, 
      isItemInCollection, 
      getItemCollections,
      isValidCollection
    } = store;

    return {
      itemCollections: getItemCollections(eid),
      addToCollection: (ecolid: number) => {
        if (!isValidCollection(ecolid)) {
          console.warn(`Invalid collection ID: ${ecolid}`);
          return Promise.resolve(false);
        }
        return addItemToCollection(eid, ecolid);
      },
      removeFromCollection: (ecolid: number) => {
        if (!isValidCollection(ecolid)) {
          console.warn(`Invalid collection ID: ${ecolid}`);
          return Promise.resolve(false);
        }
        return removeItemFromCollection(eid, ecolid);
      },
      isInCollection: (ecolid: number) => isItemInCollection(eid, ecolid)
    };
  };
};

// Export the store and hooks
export const useCollectionsStore = collectionStoreInstance;
export const useItemCollections = createItemCollectionsHook();

export default collectionStoreInstance;